import { message } from "antd";
import { useEffect, useState } from "react";
import { api } from "../../../api";
import { Button } from "../../../externalComponents/tailwind/button";
import Scheduler from "../send/shared/Scheduler";

export default function ResumeCampaignDialog({
  isOpen,
  onClose,
  selectedBatch,
  user,
  onSuccess,
}) {
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    scheduleDateTime: new Date(),
    isSendingNow: true,
  });
  const [effectiveDate, setEffectiveDate] = useState(new Date());

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${day}/${month}/${year} ${hours}:${minutes} [${timezone}]`;
  };

  const isNightTime = (date) => {
    const hours = date.getHours();
    return hours >= 22 || hours < 8;
  };

  const adjustForNightTime = (date) => {
    if (isNightTime(date)) {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(8, 0, 0, 0);
      return nextDay;
    }
    return date;
  };

  useEffect(() => {
    if (form.isSendingNow === true) {
      const updateEffectiveDate = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() + 1);
        setEffectiveDate(now);
      };
      updateEffectiveDate();
      const interval = setInterval(updateEffectiveDate, 1000);
      return () => clearInterval(interval);
    } else if (form.isSendingNow === false && form.scheduleDateTime) {
      setEffectiveDate(form.scheduleDateTime);
    }
  }, [form.isSendingNow, form.scheduleDateTime]);

  useEffect(() => {
    if (!isOpen) {
      setForm({
        scheduleDateTime: new Date(),
        isSendingNow: true,
      });
      setEffectiveDate(new Date());
      setError(null);
    }
  }, [isOpen]);

  const handleConfirmResume = async () => {
    if (!selectedBatch) {
      return;
    }

    setResuming(true);
    setError(null);
    try {
      const targetDate = adjustForNightTime(effectiveDate);

      await api.rescheduleSendingsFromStopped({
        customerId: user.customerId,
        sendingBatchId: selectedBatch.sendingBatchId,
        targetDate: targetDate.toISOString(),
        execute: true,
      });

      message.success("Campaign rescheduled successfully!");
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error rescheduling campaign:", err);
      setError(
        err.message || "Failed to reschedule campaign. Please try again.",
      );
    } finally {
      setResuming(false);
    }
  };

  if (!isOpen || !selectedBatch) {
    return null;
  }
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-5xl mx-4 h-[600px] flex flex-col">
        <h3 className="text-lg font-bold mb-4">Resume Campaign</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold text-sm text-gray-900 mb-2">
                Campaign details:
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>
                  <strong>Campaign:</strong> {selectedBatch.campaignName}
                </li>
                <li>
                  <strong>Segment:</strong> {selectedBatch.segmentName}
                </li>
                <li>
                  <strong>Remaining contacts:</strong>{" "}
                  {selectedBatch.totalContacts}
                </li>
                <li>
                  <strong>Original date:</strong>{" "}
                  {formatDate(selectedBatch.originalSendingDate)}
                </li>
                <li>
                  <strong>Effective restart date:</strong>{" "}
                  {formatDate(adjustForNightTime(effectiveDate))}
                </li>
              </ul>
            </div>

            {isNightTime(effectiveDate) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">
                  <strong>⚠️ Night time detected:</strong> The selected date
                  falls during night hours (22h-8h). Sendings will be
                  automatically rescheduled to the next day at 8:00 AM.
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">
                  <strong>Error:</strong> {error}
                </p>
              </div>
            )}
          </div>

          <div>
            <Scheduler expanded={true} form={form} setForm={setForm} />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
          <Button color="secondaryButton" onClick={onClose} disabled={resuming}>
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={handleConfirmResume}
            disabled={resuming}
          >
            {resuming ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Rescheduling...
              </span>
            ) : (
              "Resume"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
